
import { activeLocale } from './datetime';/**
 * Tabular data export helpers (S5.3).
 *
 * The Reports page (and any future tabular view) calls these to
 * turn `{columns: string[], rows: Record<string, unknown>[]}` into
 * a downloaded CSV / Excel / PDF file. All formats run client-side
 * — the backend's /export endpoint is intentionally a thin wrapper
 * around /preview now, so the user-facing download can happen
 * without a second round-trip.
 *
 * Implementation note: papaparse, xlsx and jspdf are lazy-loaded via
 * dynamic import so they don't bloat the main bundle (~360 kB
 * combined). The user only pays the load cost when they actually
 * click Export, not on every page load.
 *
 * The browser download is triggered via an anchor click on a Blob
 * URL; the URL is revoked on the next tick so the document doesn't
 * leak the URL forever.
 */

export type ExportFormat = 'CSV' | 'Excel' | 'PDF';

export interface ExportPayload {
  /** File basename, no extension. We append the right one per format. */
  filename: string;
  /** Display title to put at the top of PDFs and the first sheet name in Excel. */
  title: string;
  columns: string[];
  rows: Record<string, unknown>[];
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function _trigger_download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 200);
}

/** Excel/CSV cells expect plain strings; serialize objects, nulls. */
function _flatten_cell(v: unknown): string | number | boolean | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'object') return JSON.stringify(v);
  return v as string | number | boolean;
}

// ---------------------------------------------------------------------------
// Per-format writers (each lazy-loads its dependency)
// ---------------------------------------------------------------------------

async function exportCSV(payload: ExportPayload): Promise<void> {
  const Papa = (await import('papaparse')).default;
  const { filename, columns, rows } = payload;
  const data = rows.map((row) =>
    columns.map((c) => _flatten_cell(row[c]) ?? ''),
  );
  const csv = Papa.unparse({ fields: columns, data });
  // Add a UTF-8 BOM so Excel opens non-ASCII rows correctly.
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  _trigger_download(blob, `${filename}.csv`);
}

async function exportExcel(payload: ExportPayload): Promise<void> {
  const XLSX = await import('xlsx');
  const { filename, title, columns, rows } = payload;
  const sheetData = rows.map((row) => {
    const out: Record<string, unknown> = {};
    for (const col of columns) {
      out[col] = _flatten_cell(row[col]);
    }
    return out;
  });
  const ws = XLSX.utils.json_to_sheet(sheetData, { header: columns });
  const wb = XLSX.utils.book_new();
  // Excel limits sheet names to 31 chars and disallows certain symbols.
  const sheetName = title.slice(0, 31).replace(/[\\/?*[\]:]/g, '_') || 'Sheet1';
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  // Column auto-width: rough heuristic, max(col_name_length, max_cell_length, 10),
  // capped at 60 so wide JSON cells don't blow the layout out.
  ws['!cols'] = columns.map((c) => {
    const widest = Math.max(
      c.length,
      ...rows.map((r) => String(_flatten_cell(r[c]) ?? '').length),
    );
    return { wch: Math.min(60, Math.max(10, widest + 2)) };
  });
  const ab = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([ab], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  _trigger_download(blob, `${filename}.xlsx`);
}

async function exportPDF(payload: ExportPayload): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  const { filename, title, columns, rows } = payload;
  // Landscape A4: most attendance reports have 6-10 columns; portrait
  // squeezes them. autoTable handles paging automatically.
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  doc.setFontSize(14);
  doc.text(title, 40, 40);
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`Generated ${new Date().toLocaleString(activeLocale())}`, 40, 56);
  doc.setTextColor(0);
  autoTable(doc, {
    startY: 76,
    head: [columns],
    body: rows.map((r) => columns.map((c) => {
      const v = _flatten_cell(r[c]);
      return v === null ? '—' : String(v);
    })),
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [46, 101, 212], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });
  doc.save(`${filename}.pdf`);
}

// ---------------------------------------------------------------------------
// Public dispatch
// ---------------------------------------------------------------------------

export async function exportTable(format: ExportFormat, payload: ExportPayload): Promise<void> {
  switch (format) {
    case 'CSV':
      return exportCSV(payload);
    case 'Excel':
      return exportExcel(payload);
    case 'PDF':
      return exportPDF(payload);
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
}

/** Build a sane filename from a report name + date range. Strips
 *  characters Windows / Mac filesystems object to. */
export function buildExportFilename(
  reportName: string,
  startDate?: string,
  endDate?: string,
): string {
  const safe = reportName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const range = startDate && endDate ? `_${startDate}_${endDate}` : '';
  return `${safe || 'report'}${range}`;
}
